import { DownloadRounded, RefreshRounded, UploadRounded } from "@mui/icons-material";
import { TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { ColDef, ICellRendererParams, IServerSideDatasource, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import { SyntheticEvent, useCallback, useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import { downloadTourOperatorFile, downloadTourOperatorLog, getTourOperatorImports, getTourOperatorImportsCount, ITourOperatorImport, uploadTuiFile } from "../../../../../api/services";
import useDialog from "../../../../../hooks/useDialog";
import { ImportFilterType, TuiImportTableData } from "../../../types";
import { ImportWorkingStatus, TourOperatorWorkingStatus } from "../../../../../types";
import { AgGridCustomToolbarProps } from "../../../../../../../util/component/AgGridCustomToolbar";
import DialogCloseButton from "../../../../../components/DialogCloseButton";
import FileUploader from "../../../../../../../util/component/FileUploader";
import { setSnackbar } from "../../../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../../../util/state/globalVariables";
import { createFile } from "../../../../../utils";
import updateImportWorkingStatus from "../../../../../api/services/updateImportWorkingStatus";
import AgGridCustomTable from "../../../../../../../util/component/AgGridCustomTable";
import CustomLoader from "../../../../../../../util/component/CustomLoader";
import { useRecoilValue } from "recoil";
import { tuiFilterState } from "../../../recoil";

export const TuiTable = () => {

    const { dispatchGlobalData } = useContext(GlobalContext);

    const { t } = useTranslation('booking');

    const gridRef = useRef<AgGridReact>(null);

    const tuiListMutation = useMutation(getTourOperatorImports);
    const tuiCountMutation = useMutation(getTourOperatorImportsCount);

    const uploadDialog = useDialog();

    const filterParams = useRecoilValue(tuiFilterState);

    const columnDefs = useMemo<ColDef<TuiImportTableData>[]>(() => [
        {
            field: 'creationDate',
            headerName: t('label.timestamp'),
            valueFormatter: (params: ValueFormatterParams<TuiImportTableData>) => (
                dayjs(params.data?.creationDate).format('DD/MM/YYYY HH:mm')
            )
        },
        {
            field: 'name',
            headerName: t('label.fileName'),
            cellRenderer: (params: ICellRendererParams<TuiImportTableData>) => params?.data?.inputFile.name
        },
        {
            field: 'user',
            headerName: t('label.user'),
            valueGetter: (params: ValueGetterParams<TuiImportTableData>) => (
                params.data?.creationOperator.username
            )
        },
        {
            field: 'imported',
            headerName: t('label.records')
        },
        {
            field: 'status',
            headerName: t('label.status'),
            valueFormatter: (params: ValueFormatterParams<TuiImportTableData>) => (
                t(`import_status.${params.data?.status}`)
            )
        },
        {
            field: 'skipped',
            headerName: t('label.skipped')
        },
        {
            field: 'errorLogic',
            headerName: t('label.businessErrors')
        },
        {
            field: 'errors',
            headerName: t('label.tecnicalErrors')
        },
        {
            field: 'total',
            headerName: t('label.totals')
        },
        {
            field: 'workingStatus',
            headerName: '',
            pinned: 'right',
            suppressSizeToFit: true,
            width: 250,
            cellRenderer: (params: ICellRendererParams<TuiImportTableData>) => {
                if (!params.data?.errors && !params.data?.skipped && !params.data?.errorLogic) {
                    return undefined;
                }
                const handleChange = (e: SyntheticEvent<Element, Event>, value: NonNullable<ImportWorkingStatus>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!params.data || !value || value === params.data.workingStatus) {
                        return;
                    }
                    updateWorkingStatusMutation.mutate({
                        id: params.data.id,
                        workingStatus: value
                    });
                }
                return (
                    <Autocomplete<ImportWorkingStatus, false, true>
                        value={params.data?.workingStatus ?? TourOperatorWorkingStatus.TODO}
                        onChange={handleChange}
                        getOptionLabel={(option) => t(`import_working_status.${option}`)}
                        disableClearable
                        blurOnSelect
                        filterSelectedOptions
                        selectOnFocus={false}
                        options={[
                            TourOperatorWorkingStatus.TODO,
                            TourOperatorWorkingStatus.WIP,
                            TourOperatorWorkingStatus.DONE
                        ]}
                        disabled={params.data?.workingStatus === TourOperatorWorkingStatus.DONE}
                        renderInput={(params) => (
                            <TextField {...params} />
                        )}
                    />
                )
            }
        },
        {
            field: '_actions',
            headerName: '',
            pinned: 'right',
            suppressSizeToFit: true,
            resizable: false,
            width: 300,
            cellStyle: {
                display: 'flex',
                alignItems: 'center'
            },
            cellRenderer: (params: ICellRendererParams<TuiImportTableData>) => {
                const handleDownloadLogClick = () => {
                    if (!params.data) {
                        return;
                    }
                    downloadLogMutation.mutate({
                        id: params.data.id
                    });
                };
                const handleDownloadFileClick = () => {
                    if (!params.data) {
                        return;
                    }
                    downloadFileMutation.mutate({
                        id: params.data.id,
                    });
                };
                return (
                    <>
                        <Button
                            variant="text"
                            onClick={handleDownloadLogClick}
                            startIcon={<DownloadRounded />}
                        >
                            {t('label.log')}
                        </Button>
                        <Button
                            variant="text"
                            onClick={handleDownloadFileClick}
                            startIcon={<DownloadRounded />}
                        >
                            {t('label.file')}
                        </Button>
                    </>
                );
            }
        }
    ], [t]);

    const defaultColDef = useMemo<ColDef<TuiImportTableData>>(() => ({
        resizable: true,
        lockPinned: true,
        unSortIcon: true,
        suppressMovable: true
    }), []);

    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                const start = params.request.startRow;
                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;
                
                let count: number;

                const requestParams = {
                    filename: filterParams?.filename || '',
                    importDateFrom: filterParams?.importDateFrom?.toISOString() || null,
                    importDateTo: filterParams?.importDateTo?.toISOString() || null,
                    businessError: filterParams?.businessError?.value !== undefined ? filterParams.businessError.value : null,
                    technicalError: filterParams?.technicalError?.value !== undefined ? filterParams.technicalError.value : null,
                    skipped: filterParams?.skipped?.value !== undefined ? filterParams.skipped.value : null
                }

                tuiCountMutation.mutateAsync({
                    type: ImportFilterType.tui,
                    filename: requestParams.filename,
                    importDateFrom: requestParams.importDateFrom,
                    importDateTo: requestParams.importDateTo,
                    businessError: requestParams.businessError,
                    technicalError: requestParams.technicalError,
                    skipped: requestParams.skipped
                }).then((response: number) => {
                    count = response;

                    tuiListMutation.mutateAsync({
                        type: ImportFilterType.tui,
                        filename: requestParams.filename,
                        importDateFrom: requestParams.importDateFrom,
                        importDateTo: requestParams.importDateTo,
                        businessError: requestParams.businessError,
                        technicalError: requestParams.technicalError,
                        skipped: requestParams.skipped,
                        start,
                        limit
                    }).then((response: ITourOperatorImport[]) => {
                        params.success({
                            rowData: response,
                            rowCount: count ?? 0
                        });

                        if (count === 0) {
                            params.api.showNoRowsOverlay();
                        }
                    }).catch(() => {
                        params.fail();
                        params.api.hideOverlay();
                    });
                }).catch(() => {
                    params.fail();
                    params.api.hideOverlay();
                });
            }
            catch {
                params.fail();
                params.api.hideOverlay();
            }
        }
    }), [filterParams]);

    const toolbar = useMemo<AgGridCustomToolbarProps>(() => ({
        renderActions: () => (
            <>
                {/*Attenzione: da rimuovere quando verrà implementata la soluzione per aggiornamento automatico individuata da Giana */}
                <Tooltip
                    title={t('label.update')}
                >
                    <IconButton
                        onClick={refreshLish}
                        color="accent"
                    >
                        <RefreshRounded />
                    </IconButton>
                </Tooltip>
                <Tooltip
                    title={t('label.uploadFile')}
                >
                    <IconButton
                        onClick={uploadDialog.handleShow}
                        color="accent"
                    >
                        <UploadRounded />
                    </IconButton>
                </Tooltip>
                <Dialog
                    open={uploadDialog.open}
                    onClose={uploadDialog.handleClose}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogCloseButton onClick={uploadDialog.handleClose} />
                    <DialogTitle>
                        {t('label.uploadFile')}
                    </DialogTitle>
                    <DialogContent>
                        <FileUploader
                            isDirectUpload
                            onUpload={handleUpload}
                            acceptedFiles={{'text/csv': ['.csv'],'text/xls': ['.xls', '.xlsx'],'text/xml': ['.xml']}}
                        />
                    </DialogContent>
                </Dialog>
            </>
        )
    }), [uploadDialog]);

    const handleUpload = useCallback((file: File) => {
        uploadTuiFileMutation.mutate({
            file
        });
    }, []);

    const uploadTuiFileMutation = useMutation({
        mutationFn: uploadTuiFile,
        onSuccess: () => {
            gridRef.current?.api.hideOverlay();
            gridRef.current?.api.refreshServerSide({
                purge: true
            });
        },
        onError: () => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            })
        },
        onSettled: () => {
            uploadDialog.handleClose();
        }
    });
    const downloadLogMutation = useMutation({
        mutationFn: downloadTourOperatorLog,
        onError: () => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            })
        },
        onSuccess: (data) => {
            createFile(data.file, data.fileName)
        },
    });
    const downloadFileMutation = useMutation({
        mutationFn: downloadTourOperatorFile,
        onError: () => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        },
        onSuccess: (data) => {
            createFile(data.file, data.fileName)
        },
    });
    const updateWorkingStatusMutation = useMutation(({
        mutationFn: updateImportWorkingStatus,
        onSuccess: () => {
            gridRef.current?.api.refreshServerSide({
                purge: true
            });
        },
        onError: () => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    }));

    const refreshLish = useCallback(() => {
        gridRef.current?.api.refreshServerSide({
            purge: true
        });
    }, []);

    return (
        <>
            <AgGridCustomTable<TuiImportTableData>
                gridRef={gridRef}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                serverSideDatasource={serverSideDatasource}
                rowModelType="serverSide"
                serverSideInfiniteScroll
                paginationPageSize={25}
                cacheBlockSize={25}
                suppressDragLeaveHidesColumns
                enableCellTextSelection
                toolbar={toolbar}
            />
            <CustomLoader
                loading={
                    downloadLogMutation.isLoading ||
                    downloadFileMutation.isLoading ||
                    uploadTuiFileMutation.isLoading ||
                    updateWorkingStatusMutation.isLoading ||
                    tuiListMutation.isLoading ||
                    tuiCountMutation.isLoading
                }
            />
        </>
    );
};

export default TuiTable;